import { inject, Injectable } from '@angular/core';
import { Auth, authState, multiFactor, User } from '@angular/fire/auth';
import { collection, CollectionReference, doc, docData, DocumentReference, Firestore } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { PccAuthService } from '@ss/shared/auth';
import { Corporation, Enterprise, Facility, SimpliscreenUser } from '@ss/typings/facility';
import { ADR_ROLES, Role, ROUND_ROLES, SCREEN_ROLES } from '@ss/typings/nta-hunter';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { RouteCheckService } from './route-check.service';

export class AuthUserLoadedConfig {
	fbUser: User;

	user: SimpliscreenUser;

	ntaEnabled: boolean;

	hasSimpli360Access: boolean;

	hasEnterpriseAccess: boolean;

	hasCorporationAccess: boolean;

	requirePcc: boolean;

	hasPccToken: boolean;

	isMfaEnabled: boolean;

	hasSimpliMdsAccess: boolean;

	hasSimpliRoundsAccess: boolean;

	hasSimpliAdrAccess: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthUserService {
	private auth = inject(Auth);

	private firestore = inject(Firestore);

	private pccAuthService = inject(PccAuthService);

	fbUser: User;

	user: SimpliscreenUser;

	facility: Facility;

	loaded: Observable<AuthUserLoadedConfig>;

	enterprise: Enterprise;

	corporation: Corporation;

	constructor() {
		this.loaded = authState(this.auth).pipe(
			switchMap((user) => {
				if (!user) return of(null);

				this.fbUser = user;
				return docData<SimpliscreenUser>(doc(collection(this.firestore, '/users') as CollectionReference<SimpliscreenUser>, user.uid) as DocumentReference<SimpliscreenUser>, { idField: 'userId' });
			}),
			switchMap((user: SimpliscreenUser) => {
				let facility$: Observable<Facility> = of(null);
				let enterprise$: Observable<Enterprise> = of(null);
				let corporation$: Observable<Corporation> = of(null);
				if (user) {
					this.user = user;
					const facilityId = user.currentFacilityId;
					facility$ = docData<Facility>(doc(collection(this.firestore, '/facilities') as CollectionReference<Facility>, facilityId) as DocumentReference<Facility>, { idField: 'facilityId' });
					if (user.enterpriseId) {
						enterprise$ = docData<Enterprise>(doc(collection(this.firestore, '/enterprises') as CollectionReference<Enterprise>, user.enterpriseId) as DocumentReference<Enterprise>, { idField: 'enterpriseId' });
					}
					if (user.corporationId) {
						corporation$ = docData<Corporation>(doc(collection(this.firestore, '/corporations') as CollectionReference<Corporation>, user.corporationId) as DocumentReference<Corporation>, { idField: 'corporationId' });
					}
				}

				return combineLatest([facility$, enterprise$, corporation$]);
			}),
			tap(([facility, enterprise, corporation]) => {
				if (facility) {
					this.facility = new Facility(facility);
				}
				if (enterprise) this.enterprise = new Enterprise(enterprise);
				if (corporation) this.corporation = new Corporation(corporation);
			}),
			map(() => {
				const user = this.facility?.ntaUsers.find((u) => u.userId === this.user.userId);
				const hasCorporationAccess = !!(this.corporation && this.corporation.ntaUsers[this.user.userId]);
				const hasEnterpriseAccess = !!(this.enterprise && this.enterprise.ntaUsers[this.user.userId]) || this.user?.isSuperAdmin || hasCorporationAccess && this.corporation.enterpriseIds.includes(this.enterprise?.enterpriseId || '');
				const hasSimpli360AccessThroughSuperAdmin = this.user?.isSuperAdmin;
				const hasSimpli360AccessThroughCorporation = hasCorporationAccess && this.corporation.enterpriseIds.includes(this.facility.enterpriseId);
				const hasSimpli360AccessThroughEnterprise = hasEnterpriseAccess && this.enterprise?.facilityIds?.includes(this.facility.facilityId);
				const hasSimpliMdsAccess = hasSimpli360AccessThroughSuperAdmin || hasSimpli360AccessThroughCorporation || SCREEN_ROLES.includes(user?.role as Role) || hasSimpli360AccessThroughEnterprise;
				const hasSimpliRoundsAccess = hasSimpli360AccessThroughSuperAdmin || hasSimpli360AccessThroughCorporation || ROUND_ROLES.includes(user?.role as Role) || hasSimpli360AccessThroughEnterprise;
				const hasSimpliAdrAccess = hasSimpli360AccessThroughSuperAdmin || hasSimpli360AccessThroughCorporation || ADR_ROLES.includes(user?.role as Role) || hasSimpli360AccessThroughEnterprise;
				const hasSimpli360Access = hasSimpliMdsAccess || hasSimpliRoundsAccess || hasSimpliAdrAccess;

				let isMfaEnabled = false;
				if (this.fbUser) {
					const { enrolledFactors } = multiFactor(this.fbUser);
					isMfaEnabled = enrolledFactors?.length > 0;
				}
				return {
					fbUser: this.fbUser,
					user: this.user,
					ntaEnabled: this.facility?.ntaEnabled ?? false,
					hasSimpliMdsAccess,
					hasSimpliRoundsAccess,
					hasSimpliAdrAccess,
					hasSimpli360Access,
					hasEnterpriseAccess,
					hasCorporationAccess,
					requirePcc: this.facility?.features.includes('REQUIRE_PCC'),
					hasPccToken: !!this.pccAuthService.getToken(),
					isMfaEnabled
				};
			}),
		);
	}
}

export const enableMfaGuard = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}
		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}
		if (!loaded.ntaEnabled) {
			router.navigate(['account/coming-soon']);
			return false;
		}
		if (!loaded.hasSimpli360Access) {
			router.navigate(['account/unauthorized']);
			return false;
		}
		if (loaded.user.hasTempPassword) {
			router.navigate(['account/update-password']);
			return false;
		}
		if (!loaded.fbUser.emailVerified) {
			router.navigate(['account/verify-email']);
			return false;
		}
		if (loaded.isMfaEnabled) {
			router.navigate(['']);
			return false;
		}
		return true;
	}));
};

export const verifyEmailGaurd = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}
		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}
		if (!loaded.ntaEnabled) {
			router.navigate(['account/coming-soon']);
			return false;
		}
		if (!loaded.hasSimpli360Access) {
			router.navigate(['account/unauthorized']);
			return false;
		}
		if (loaded.user.hasTempPassword) {
			router.navigate(['account/update-password']);
			return false;
		}
		if (loaded.fbUser.emailVerified) {
			router.navigate(['']);
			return false;
		}
		return true;
	}));
};

export const updatePasswordGuard = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}
		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}
		if (!loaded.ntaEnabled) {
			router.navigate(['account/coming-soon']);
			return false;
		}
		if (!loaded.hasSimpli360Access) {
			router.navigate(['account/unauthorized']);
			return false;
		}
		if (!loaded.user.hasTempPassword) {
			router.navigate(['']);
			return false;
		}
		return true;
	}));
};

export const unauthorizedGuard = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}
		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}
		if (!loaded.ntaEnabled) {
			router.navigate(['account/coming-soon']);
			return false;
		}
		if (loaded.hasSimpli360Access) {
			router.navigate(['']);
			return false;
		}
		return true;
	}));

};

export const comingSoonGuard = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}
		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}
		if (loaded.ntaEnabled) {
			router.navigate(['']);
			return false;
		}
		return true;
	}));
};

export const mainGuard = (_route, state) => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	const routeCheck = inject(RouteCheckService);

	return authUser.loaded.pipe(map((loaded) => {
		if (!loaded.fbUser) {
			router.navigate(['account/signin']);
			return false;
		}

		if (loaded.requirePcc && !loaded.hasPccToken) {
			router.navigate(['account/signin-pcc']);
			return false;
		}

		if (!loaded.ntaEnabled) {
			router.navigate(['account/coming-soon']);
			return false;
		}

		if (!loaded.hasSimpli360Access) {
			router.navigate(['account/unauthorized']);
			return false;
		}

		if (loaded.user.hasTempPassword) {
			router.navigate(['account/update-password']);
			return false;
		}

		if (!loaded.fbUser.emailVerified) {
			router.navigate(['account/verify-email']);
			return false;
		}

		if (!loaded.isMfaEnabled) {
			router.navigate(['account/mfa']);
			return false;
		}

		if (routeCheck.url && (state?.url === '/' || state?.url === '')) {
			routeCheck.navigateToExistingUrl();
			return false;
		}

		return true;
	}));
};

export const enterpriseGuard = () => {
	const authUser = inject(AuthUserService);
	const router = inject(Router);

	return authUser.loaded.pipe(
		take(1),
		map((loaded) => {
			if (!loaded.hasEnterpriseAccess && !loaded.hasCorporationAccess) {
				if (loaded.hasSimpliMdsAccess) return router.parseUrl('/simplimds');
				if (loaded.hasSimpliRoundsAccess) return router.parseUrl('/simplirounds');
				if (loaded.hasSimpliAdrAccess) return router.parseUrl('/simpliadr');
				return router.parseUrl('/account/unauthorized');
			}

			return true;
		})
	);
};
